import {AlertProps, AlertVariants, Flag, IconButton, Tooltip} from 'platform/components';
import {
  Box,
  getIconFromSeverity,
  Heading,
  HStack,
  Icon,
  Severity,
  Show,
  Text,
} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {reject, take} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {EntityHeaderFlags} from '../../EntityHeader/components/EntityHeaderFlags';
import type {EntityHeaderFlag} from '../../EntityHeader/EntityHeader';
import type {EntityHeaderAction} from '../../EntityHeader/types/EntityHeaderAction';
import type {PageSectionNavigation} from '../../Page/Page';
import type {DetailTemplateHeader} from '../DetailTemplate';
import {CompactHeaderActions} from './CompactHeaderActions';
import {SectionSwitcher} from './SectionSwitcher';

// The flags that stay in the row. The others are behind a "+N" flag with a tooltip.
const MAX_VISIBLE_FLAGS = 3;

const isRedFlag = (flag: EntityHeaderFlag) => flag.colorScheme === 'red';

// Red flags, such as Overdue, take the places in the row first and the other flags fill the rest.
// The row keeps the order of the page.
const splitFlags = (flags: EntityHeaderFlag[]) => {
  const redFlags = take(MAX_VISIBLE_FLAGS, flags.filter(isRedFlag));
  const otherFlags = take(MAX_VISIBLE_FLAGS - redFlags.length, reject(isRedFlag, flags));
  const isVisible = (flag: EntityHeaderFlag) =>
    redFlags.includes(flag) || otherFlags.includes(flag);

  return {visibleFlags: flags.filter(isVisible), hiddenFlags: reject(isVisible, flags)};
};

// A long record title is cut off here, so it does not push the actions into the "more" menu.
const TITLE_MAX_WIDTH = 80;

// A new set of actions starts the fitting again, because the measured widths belong to the old set.
const getActionsKey = (actions: EntityHeaderAction[] | undefined) =>
  (actions ?? [])
    .map((action) => ('title' in action ? `${action.type}:${action.title}` : action.type))
    .join('|');

// The severity of each alert variant, so an alert keeps the icon and color of its banner.
const getAlertSeverity = (variant: AlertVariants | undefined) =>
  match(variant ?? 'warning')
    .returnType<Severity>()
    .with('info', () => 'informational')
    .with('warning', () => 'warning')
    .with('success', () => 'success')
    .with('error', () => 'danger')
    .exhaustive();

const getAlertText = (alert: AlertProps) =>
  match(alert)
    .with({title: Pattern.string}, (matchedAlert) => matchedAlert.title)
    .with({message: Pattern.string}, (matchedAlert) => matchedAlert.message)
    .with({message: Pattern.array(Pattern.string)}, (matchedAlert) =>
      matchedAlert.message.join(' ')
    )
    .otherwise(() => undefined);

interface CompactEntityHeaderProps extends TestIdProps {
  header: DetailTemplateHeader | undefined;
  sectionNavigation: PageSectionNavigation;
  onExpand: VoidFunction;
}

/**
 * One-row record header: what the user needs while working in a list (section, record, state,
 * warnings, total, actions). The full header is one click away.
 */
export function CompactEntityHeader(props: CompactEntityHeaderProps) {
  const alerts = props.header?.alerts ?? [];
  const {visibleFlags, hiddenFlags} = splitFlags(props.header?.flags ?? []);

  const expandHeaderLabel = i18n.t('general.actions.expandHeader');

  return (
    // Sticky, so the record stays in view when the page scrolls as a whole document.
    <Box
      position="sticky"
      top={0}
      zIndex="PAGE_NAVIGATION"
      paddingHorizontal={4}
      paddingVertical={2}
      backgroundColor="palettes.white.10.100"
      borderBottom="1px solid"
      borderColor="palettes.neutral.40.100"
      data-testid={suffixTestId('compactHeader', props)}
    >
      <HStack align="center" spacing={3}>
        <SectionSwitcher
          sectionNavigation={props.sectionNavigation}
          data-testid={props['data-testid']}
        />
        <Show when={props.header?.icon}>{(icon) => <Icon value={icon} size={5} />}</Show>
        <Box maxWidth={TITLE_MAX_WIDTH} minWidth={0}>
          <Heading size={4} isSingleLine>
            {props.header?.title}
          </Heading>
        </Box>
        <HStack align="center" spacing={1}>
          <EntityHeaderFlags
            flags={visibleFlags}
            dropdownFlags={props.header?.dropdownFlags}
            isLoading={props.header?.isEntityLoading}
            data-testid={props['data-testid']}
          />
          <Show when={hiddenFlags.length > 0}>
            <Tooltip
              label={
                <HStack spacing={1} wrap>
                  {hiddenFlags.map((flag) => (
                    <Flag key={`${flag.label}-${flag.colorScheme}`} {...flag} />
                  ))}
                </HStack>
              }
            >
              <Flag
                label={`+${hiddenFlags.length}`}
                colorScheme="neutral"
                data-testid={suffixTestId('compactHeaderHiddenFlags', props)}
              />
            </Tooltip>
          </Show>
        </HStack>
        {/* Alerts never disappear without a user action: each alert keeps the icon of its variant,
            its text is in the tooltip, and a click expands the full header. */}
        <Show when={alerts.length > 0}>
          <HStack align="center" spacing={1}>
            {alerts.map((alert, index) => {
              const alertText = getAlertText(alert);
              const severity = getAlertSeverity(alert.variant);

              return (
                <Tooltip key={`${alert.variant}-${alert.title}-${alert.message}`} label={alertText}>
                  <IconButton
                    icon={getIconFromSeverity(severity)}
                    severity={severity}
                    aria-label={alertText}
                    onClick={props.onExpand}
                    data-testid={suffixTestId(`compactHeaderAlert-[${index}]`, props)}
                  />
                </Tooltip>
              );
            })}
          </HStack>
        </Show>
        <CompactHeaderActions
          key={getActionsKey(props.header?.actions)}
          actions={props.header?.actions}
          data-testid={props['data-testid']}
        >
          <Show
            when={
              isNotNilOrEmpty(props.header?.primaryParameter)
                ? props.header?.primaryParameter
                : null
            }
          >
            {(primaryParameter) => (
              <HStack align="baseline" spacing={2}>
                <Heading size={4} isSingleLine>
                  {primaryParameter}
                </Heading>
                <Show
                  when={
                    isNotNilOrEmpty(props.header?.secondaryParameter)
                      ? props.header?.secondaryParameter
                      : null
                  }
                >
                  {(secondaryParameter) => (
                    <Text size="small" color="tertiary" noWrap>
                      {secondaryParameter}
                    </Text>
                  )}
                </Show>
              </HStack>
            )}
          </Show>
        </CompactHeaderActions>
        <Tooltip label={expandHeaderLabel}>
          <IconButton
            icon="navigation/unfold_more"
            priority="secondary"
            aria-label={expandHeaderLabel}
            onClick={props.onExpand}
            data-testid={suffixTestId('expandHeader', props)}
          />
        </Tooltip>
      </HStack>
    </Box>
  );
}
